import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, FileText, Download, Archive, ArchiveRestore, Plus, Trash2 } from "lucide-react";
import type { PromotionBase } from "nautika-types";

type Registration = {
  id: string;
  createdAt?: string;
  archived?: boolean | string | null;
  subtotalAmount?: number;
  promotions?: PromotionBase[];
  paymentDetails: { card?: number; ancv?: number; cash?: number; check?: number };
  adults: Array<{ id: string; firstName: string; lastName: string; phone?: string | null; email?: string | null }>;
  children: Array<{ id: string; firstName: string; lastName: string; birthDate: string | Date | number }>;
  remainingAmount: number;
};

export type RegistrationCardProps = {
  registration: Registration;
  activeTab: string;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  archivePending?: boolean;
  unarchivePending?: boolean;
  onClick?: () => void;
};

const calculateAge = (date: string | Date | number) => {
  const birth = new Date(date);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const getTruncatedTitle = (children: Registration["children"]) => {
  const names = children.map((child) => child.firstName);
  return names.join(" · ");
};

export function RegistrationCard(props: RegistrationCardProps) {
  const { registration, activeTab, onArchive, onUnarchive, archivePending, unarchivePending, onClick } = props;

  const isPriceMissing = (registration.subtotalAmount ?? 0) === 0;
  const hasNoAdults = (registration.adults?.length ?? 0) === 0;

  return (
    <Card
      key={registration.id}
      className="relative cursor-pointer overflow-hidden border-2 border-transparent bg-white transition-all hover:bg-gray-50 hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader className="flex items-center justify-between pb-0">
        <CardTitle className="text-md mr-2 flex-1 truncate font-bold">{getTruncatedTitle(registration.children)}</CardTitle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 rounded-md p-0" onClick={(e) => e.stopPropagation()}>
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled>
              <FileText size={16} className="mr-2" />
              Imprimer la fiche
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Download size={16} className="mr-2" />
              Exporter en PDF
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {activeTab === "archived" ? (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnarchive(registration.id);
                  }}
                  className="focus:text-blue-700"
                  disabled={!!unarchivePending}
                >
                  <ArchiveRestore size={16} className="mr-2 focus:text-blue-700" />
                  Désarchiver l'inscription
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Trash2 size={16} className="mr-2" />
                  Supprimer définitivement
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(registration.id);
                }}
                className="text-red-600"
                disabled={!!archivePending}
              >
                <Archive size={16} className="mr-2" />
                {archivePending ? "Archivage..." : "Archiver l'inscription"}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="space-y-4">
          <div>
            <p className="mb-1 text-xs text-gray-500">Reste à payer (€)</p>
            {isPriceMissing ? (
              <Badge className="rounded-sm bg-orange-100 px-2 py-1 text-xs text-orange-700">Prix à compléter</Badge>
            ) : (
              <Badge
                className={`rounded-sm px-2 py-1 text-xs ${
                  registration.remainingAmount > 0
                    ? "bg-red-100 text-red-700"
                    : registration.remainingAmount < 0
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700"
                }`}
              >
                {registration.remainingAmount < 0 ? `${Math.abs(registration.remainingAmount).toFixed(2)}€ de surplus` : `${registration.remainingAmount.toFixed(2)}€`}
              </Badge>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs text-gray-500">Parents</p>
            <div className="flex flex-wrap gap-1">
              {hasNoAdults ? (
                <Badge className="rounded-sm bg-orange-100 px-2 py-1 text-xs text-orange-700">Adultes à renseigner</Badge>
              ) : (
                (() => {
                  const totalPeople = registration.adults.length + registration.children.length;
                  const adultsToShow = totalPeople > 5 ? Math.min(registration.adults.length, 2) : registration.adults.length;
                  const hiddenAdults = registration.adults.length - adultsToShow;
                  return (
                    <>
                      {registration.adults.slice(0, adultsToShow).map((adult) => (
                        <div key={adult.id} className="inline-block w-fit rounded bg-gray-100 px-2 py-1 text-sm font-bold whitespace-nowrap">
                          {adult.firstName} {adult.lastName}
                        </div>
                      ))}
                      {hiddenAdults > 0 && (
                        <Badge variant="outline" className="inline-flex h-6 w-fit items-center border-dashed px-2 text-xs whitespace-nowrap">
                          <Plus size={14} className="mr-1" />
                          {hiddenAdults} autre adulte{hiddenAdults > 1 ? "s" : ""}
                        </Badge>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs text-gray-500">Enfants</p>
            <div className="flex flex-wrap items-center gap-1">
              {(() => {
                const totalPeople = (registration.adults?.length ?? 0) + registration.children.length;
                const adultsToShow = totalPeople > 5 ? Math.min(registration.adults?.length ?? 0, 2) : (registration.adults?.length ?? 0);
                const childrenToShow = totalPeople > 5 ? Math.min(registration.children.length, 3, 5 - adultsToShow) : registration.children.length;
                const hiddenChildren = registration.children.length - childrenToShow;
                return (
                  <>
                    {registration.children.slice(0, childrenToShow).map((child) => (
                      <div key={child.id} className="inline-block w-fit rounded bg-gray-100 px-2 py-1 text-sm font-bold whitespace-nowrap">
                        {child.firstName} {child.lastName} ({calculateAge(child.birthDate)} ans)
                      </div>
                    ))}
                    {hiddenChildren > 0 && (
                      <Badge variant="outline" className="inline-flex h-6 w-fit items-center border-dashed px-2 text-xs whitespace-nowrap">
                        <Plus size={14} className="mr-1" />
                        {hiddenChildren} autre enfant{hiddenChildren > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
